import { ArrowUpRight, FileText, Lightbulb, Plus, Search, Sparkles, type LucideIcon } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChatThread } from "@/features/copiloto/ChatThread";
import { CopilotoComposer } from "@/features/copiloto/CopilotoComposer";
import { useCopiloto } from "@/features/copiloto/CopilotoContext";
import { MestraIcon } from "@/features/copiloto/MestraIcon";

import { useDismiss } from "@/shared/hooks/useDismiss";
const CLOSE_ANIMATION_MS = 160;

// Os quebra-gelos são cadastro do admin, então o ícone é só ornamento por
// posição — não tenta adivinhar o assunto da pergunta.
const ICEBREAKER_ICONS: LucideIcon[] = [Sparkles, Lightbulb, FileText, Search];

/**
 * A Mestra Help: a conversa inteira mora neste balão da topbar — não há
 * mais tela própria (2026-10-13, o mesmo desenho do hub). O estado é do
 * `CopilotoProvider`, montado uma vez na casca, então fechar e reabrir o
 * balão devolve a MESMA conversa; recomeçar é o "Nova conversa".
 */
export function CopilotoQuickBox({
  anchorRef,
  onClose
}: {
  token: string;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  const { messages, sending, icebreakers, defaultMode, sendMessage, startNewConversation } = useCopiloto();
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const [closing, setClosing] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.bottom + 12,
          right: Math.max(16, window.innerWidth - rect.right)
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [messages]);

  const requestClose = () => {
    setClosing(true);
    window.setTimeout(onClose, CLOSE_ANIMATION_MS);
  };

  useDismiss(true, requestClose);

  const inConversation = messages.some(message => !message.hidden);

  return createPortal(
    <>
      <div className="copiloto-quickbox-scrim" onClick={requestClose}/>
      <div
        className={`copiloto-quickbox-anchor ${inConversation ? "is-conversation" : ""}`}
        style={{ top: position?.top ?? 0, right: position?.right ?? 16, visibility: position ? "visible" : "hidden" }}
      >
        <div className={`copiloto-quickbox ${closing ? "is-closing" : "is-opening"}`} role="dialog" aria-label="Mestra Help">
          <div className="mestra-help-head">
            <span className="mestra-help-title">
              <span className="mestra-help-avatar" aria-hidden="true"><MestraIcon size={14}/></span>
              <span className="mestra-help-title-text">
                <strong>Mestra <span className="mestra-help-accent">Help</span></strong>
                {!inConversation && <small>Tire dúvidas sobre a plataforma e seus dados</small>}
              </span>
            </span>
            {inConversation && (
              <button type="button" className="copiloto-quickbox-btn" onClick={startNewConversation} disabled={sending}>
                <Plus size={14} aria-hidden="true"/> Nova conversa
              </button>
            )}
          </div>
          {inConversation ? (
            <div className="mestra-help-thread" role="log" aria-live="polite" aria-label="Conversa com a Mestra Help">
              <ChatThread messages={messages} mode={defaultMode} disabled={sending}/>
              <div ref={threadEndRef}/>
            </div>
          ) : icebreakers.length > 0 && (
            <div className="mestra-help-icebreakers">
              <p className="mestra-help-icebreakers-label">Comece por aqui</p>
              <div className="mestra-help-icebreakers-grid">
                {icebreakers.slice(0, 4).map((question, index) => {
                  const Icon = ICEBREAKER_ICONS[index % ICEBREAKER_ICONS.length];
                  return (
                  <button
                    key={question}
                    type="button"
                    title={question}
                    disabled={sending}
                    onClick={() => void sendMessage(question, defaultMode)}
                  >
                    <span className="mestra-help-icebreaker-icon" aria-hidden="true"><Icon size={13}/></span>
                    <span className="mestra-help-icebreaker-text">{question}</span>
                    <ArrowUpRight size={14} className="mestra-help-icebreaker-arrow" aria-hidden="true"/>
                  </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mestra-help-compose">
            <CopilotoComposer
              mode={defaultMode}
              onSend={content => void sendMessage(content, defaultMode)}
              disabled={sending}
              placeholder={inConversation ? "Responder…" : "Pergunte algo à Mestra Help…"}
              hideDisclaimer
            />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
